import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  ClipboardList,
  FilePlus2,
  Grid3x3,
  LogOut,
  Menu,
  X,
  Hexagon,
  Briefcase,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const consultantNav = [
  { to: "/app/dashboard", label: "Panel", icon: LayoutDashboard },
  { to: "/app/jobs", label: "Puestos", icon: Briefcase },
  { to: "/app/matrix", label: "Metodología", icon: Grid3x3 },
];
const candidateNav = [
  { to: "/app/portal", label: "Mis evaluaciones", icon: ClipboardList },
  { to: "/app/assessment/new", label: "Nueva evaluación", icon: FilePlus2 },
  { to: "/app/matrix", label: "Metodología", icon: Grid3x3 },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "candidate" ? candidateNav : consultantNav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="px-6 py-7 border-b border-slate-800">
        <img src="/brand/talenthackers-logo.png" alt="Talent Hackers" className="h-9 w-auto object-contain object-left" data-testid="sidebar-logo" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary mt-2">Agentic Deputy</div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
              }`
            }
          >
            <item.icon size={18} strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="px-2 mb-3">
          <div className="text-sm text-slate-200 truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <div className="mt-1.5 inline-block rounded bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
            {user?.role === "candidate" ? "Candidato" : "Consultor"}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start gap-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10"
        >
          <LogOut size={18} strokeWidth={1.5} /> Cerrar sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] bg-[#0f172a] border-r border-slate-800 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/brand/talenthackers-logo.png" alt="Talent Hackers" className="h-6 w-auto object-contain" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-primary">Deputy</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-slate-300">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            className="absolute inset-y-0 left-0 w-[260px] bg-[#0f172a] border-r border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarInner />
          </motion.aside>
        </div>
      )}

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-7xl px-5 py-8 md:px-10 md:py-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
